import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { notificationService, Notification } from '@services/notificationService'
import { Bell, Check, Clock, AlertCircle, FileText, DollarSign, AlertTriangle, ExternalLink } from 'lucide-react'
import { toast } from 'react-hot-toast'

interface NotificationDropdownProps {
  onClose?: () => void
  onUnreadCountChange?: (count: number) => void
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadNotifications()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const loadNotifications = async () => {
    try {
      setLoading(true)
      const response = await notificationService.getNotifications({
        limit: 10
      })
      setNotifications(response.notifications)
      setUnreadCount(response.unreadCount)
    } catch (error) {
      console.error('Error loading notifications:', error)
      toast.error('載入通知失敗')
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId)
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      )
      const newCount = Math.max(0, unreadCount - 1)
      setUnreadCount(newCount)
      onUnreadCountChange?.(newCount)
    } catch (error) {
      console.error('Error marking notification as read:', error)
      toast.error('標記為已讀失敗')
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'TIMESHEET_REMINDER':
      case 'TIMESHEET_OVERDUE':
      case 'TIMESHEET_DEADLINE_APPROACHING':
        return <Clock className="w-4 h-4 text-blue-500" />
      case 'TIMESHEET_SUBMITTED':
      case 'TIMESHEET_APPROVED':
        return <Check className="w-4 h-4 text-green-500" />
      case 'TIMESHEET_REJECTED':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      case 'BUDGET_WARNING':
      case 'BUDGET_EXCEEDED':
        return <DollarSign className="w-4 h-4 text-yellow-500" />
      case 'WORK_TIME_WARNING':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />
      default:
        return <FileText className="w-4 h-4 text-gray-500" />
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) {
      return '剛剛'
    } else if (diffInHours < 24) {
      return `${diffInHours}小時前`
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}天前`
    } else {
      return date.toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' })
    }
  }

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-primary-200 z-50 max-h-96 overflow-hidden"
    >
      <div className="px-4 py-3 bg-primary-50 border-b border-primary-200">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-secondary-800">通知</h3>
          <Link 
            to="/notifications" 
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
            onClick={onClose}
          >
            <span>查看全部</span>
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <p className="text-xs text-secondary-600 mt-1">
          {unreadCount > 0 ? `${unreadCount} 則未讀` : '沒有未讀通知'}
        </p>
      </div>

      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-secondary-600">
            <Bell className="w-8 h-8 mx-auto mb-2 text-secondary-300" />
            <p className="text-sm">沒有通知</p>
          </div>
        ) : (
          <div className="divide-y divide-primary-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-4 py-3 hover:bg-primary-25 transition-colors ${
                  !notification.isRead ? 'bg-primary-25' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          !notification.isRead ? 'text-secondary-900' : 'text-secondary-700'
                        }`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-secondary-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-secondary-500 mt-1">
                          {formatDate(notification.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                        )}
                        {!notification.isRead && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs text-primary-600 hover:text-primary-700 transition-colors px-1"
                            title="標記為已讀"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationDropdown
